/**
 * @file CompetiteursDebutantsMemeBox.tsx
 * @summary Article de blog — CompetiteursDebutantsMemeBox
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, FAQList, FurtherReading, SourcesSection, AdaptationTable, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/competiteurs-crossfit-chistera-debutants";
const TITLE = "Compétiteurs et débutants dans la même box : comment ça marche";
const DESC = "Comment CrossFit Chistera fait cohabiter compétiteurs aguerris et grands débutants dans les mêmes créneaux : scaling, RX, programmation, ambiance.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Je débute, est-ce que je vais ralentir le groupe ?", a: "Non. Chaque WOD est scalé individuellement (charge, mouvement, volume). Vous travaillez à votre intensité, à côté d'un compétiteur qui travaille à la sienne." },
  { q: "Je suis compétiteur, vais-je vraiment progresser en classe collective ?", a: "Oui pour le développement général. En complément, le coaching individuel cible vos faiblesses spécifiques et la périodisation autour de vos compétitions." },
  { q: "Y a-t-il des créneaux 'élite' réservés ?", a: "Non. Tous les créneaux sont mixtes — c'est volontaire. Cela évite la création d'une 'caste' et garde l'ambiance saine." },
  { q: "Et si je veux faire une compétition ?", a: "On vous accompagne. Voir notre article sur le coaching individuel compétition." },
];

export default function CompetiteursDebutantsMemeBox() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Compétiteurs et débutants dans la même box"
      breadcrumbLabel="Compétiteurs & débutants"
      category="Méthode · Vie de la box"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-12" readingMinutes={8}
      ctaVariant="default"
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-12", author, reviewer, faq })}
    >
      <EnBref>
        <p>Chez Chistera, un compétiteur HYROX et une personne qui découvre la barre travaillent côte à côte. Ce n'est pas du marketing : c'est rendu possible par un <strong>scaling systématique</strong>, des coachs qui adaptent en temps réel, et une culture où l'effort relatif compte plus que la performance absolue.</p>
      </EnBref>

      <h2>Le principe : RX, intermédiaire, scaled</h2>
      <p>Chaque WOD est écrit avec trois niveaux de référence :</p>
      <ul>
        <li><strong>RX</strong> — la version prescrite, charge et mouvement standards.</li>
        <li><strong>Intermédiaire</strong> — charge ou volume réduits, mouvement identique.</li>
        <li><strong>Scaled</strong> — mouvement adapté (ex. ring rows au lieu de pull-ups), volume diminué.</li>
      </ul>
      <p>Et au-delà de ces trois niveaux, le coach ajuste pour <em>vous</em>, en fonction de votre forme du jour, vos blessures éventuelles, votre objectif.</p>

      <h2>Exemples de scaling sur un même WOD</h2>
      <AdaptationTable headers={["Compétiteur (RX)", "Débutant (scaled)"]} rows={[
        { standard: "21-15-9 thrusters 43 kg / pull-ups", adapted: "21-15-9 thrusters barre vide / ring rows" },
        { standard: "5 rounds : 400 m run, 15 box jumps 60 cm", adapted: "5 rounds : 400 m row, 15 step-ups 40 cm" },
        { standard: "AMRAP 20 min : 5 muscle-ups, 10 HSPU, 15 wall balls 9 kg", adapted: "AMRAP 20 min : 5 pull-ups assistés, 10 push-ups inclinés, 15 wall balls 4 kg" },
      ]} />

      <h2>Pourquoi ça marche</h2>
      <ol>
        <li><strong>Le chrono unit tout le monde.</strong> Quand le compteur tourne, l'effort est partagé — peu importe la charge.</li>
        <li><strong>Les compétiteurs montrent l'exemple technique.</strong> Voir un mouvement bien exécuté à côté de soi vaut mille consignes.</li>
        <li><strong>Les débutants rappellent l'essentiel.</strong> Le plaisir, la progression, l'envie de revenir — c'est contagieux dans les deux sens.</li>
      </ol>

      <h2>Et concrètement, à votre première séance ?</h2>
      <p>Vous arrivez. Le coach vous présente, vous explique le WOD, vous propose une version adaptée. Vous travaillez à votre rythme, sans pression. Personne ne vous regardera de travers parce que vous êtes débutant — au contraire, c'est plutôt l'inverse qui se passe : tout le monde vient vous encourager. Pour ceux qui veulent vivre ça d'abord, voir <Link to="/blog/comment-se-passe-une-seance-crossfit">"Comment se passe une séance"</Link>.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Coaching individuel pour la compétition (HYROX, ATHX, CrossFit)", to: "/blog/coaching-individuel-competition-hyrox-athx-crossfit" },
        { label: "Comment se passe une séance de CrossFit", to: "/blog/comment-se-passe-une-seance-crossfit" },
        { label: "Avis CrossFit Chistera", to: "/blog/avis-crossfit-chistera" },
      ]} />

      <SourcesSection items={[
        { label: "CrossFit — Scaling Methodology", href: "https://www.crossfit.com/essentials/scaling-crossfit-workouts" },
      ]} />
    </ArticleLayout>
  );
}
